import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Navigate, useNavigate, useSearchParams } from 'react-router';

import { AuthLayout } from '../../components/auth/auth-layout.tsx';
import { CodeInput } from '../../components/auth/code-input.tsx';
import { Alert } from '../../components/ui/alert.tsx';
import { Button } from '../../components/ui/button.tsx';
import { Field, Input } from '../../components/ui/field.tsx';
import { Loading } from '../../components/ui/skeleton.tsx';
import { api, ApiError, errorMessage } from '../../lib/api.ts';
import { safeReturnPath } from '../../lib/navigation.ts';
import { refreshSession, useSession } from '../../lib/session.ts';

/** Second login step: the authenticator app's code, or a single-use recovery code. */
export function Verify2faPage() {
  const { data: session, isPending } = useSession();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [mode, setMode] = useState<'totp' | 'recovery'>('totp');
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  // Unchecked by default; only with the app's code (never with a recovery code).
  const [remember, setRemember] = useState(false);
  const back = safeReturnPath(params.get('volver'));

  if (isPending) return <Loading />;
  if (!session) return <Navigate to="/iniciar-sesion" replace />;
  if (session.mfa === 'verified') return <Navigate to={back} replace />;
  if (session.mfa === 'enroll')
    return <Navigate to={`/activar-2fa${params.size ? `?${params.toString()}` : ''}`} replace />;

  const submit = async (value: string) => {
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      const result = await api<{ remaining?: number } | undefined>(
        mode === 'totp' ? '/auth/2fa/verify' : '/auth/2fa/recovery',
        {
          method: 'POST',
          body: mode === 'totp' ? { code: value, rememberDevice: remember } : { code: value },
        },
      );
      await refreshSession(queryClient);
      const remaining = result?.remaining;
      await navigate(
        back,
        remaining !== undefined ? { state: { recoveryRemaining: remaining } } : {},
      );
    } catch (err) {
      setCode('');
      if (err instanceof ApiError && err.code === 'too_many_attempts') {
        // The API already revoked this session. Do not clear the cached session here:
        // this page's own "no session" redirect (without the notice) would win. The
        // next login refreshes the session anyway.
        await navigate('/iniciar-sesion', { state: { notice: err.message } });
        return;
      }
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <AuthLayout
      title="Verificación en dos pasos"
      subtitle={
        mode === 'totp'
          ? 'Escribe el código de 6 dígitos de tu aplicación de autenticación.'
          : 'Escribe uno de tus códigos de respaldo. Cada código sirve una sola vez.'
      }
    >
      {error && <Alert tone="danger">{error}</Alert>}
      <form
        className="space-y-4"
        onSubmit={(event) => {
          event.preventDefault();
          void submit(code);
        }}
      >
        {mode === 'totp' && session.trustedDeviceDays !== null && (
          // Before the code: the code is sent as soon as its 6 digits are typed.
          <label className="flex items-start gap-2 text-sm">
            <input
              type="checkbox"
              className="mt-0.5 size-5 shrink-0"
              checked={remember}
              onChange={(event) => {
                setRemember(event.target.checked);
              }}
            />
            <span>
              Recordar este dispositivo durante {session.trustedDeviceDays} días
              <span className="block text-muted-foreground">
                No te pediremos el código en este navegador (sí la contraseña). Úsalo solo en tu
                propio equipo.
              </span>
            </span>
          </label>
        )}
        {mode === 'totp' ? (
          <Field label="Código de verificación">
            {(a11y) => (
              <CodeInput
                {...a11y}
                value={code}
                onChange={setCode}
                onComplete={(value) => {
                  void submit(value);
                }}
              />
            )}
          </Field>
        ) : (
          <Field label="Código de respaldo" hint="Por ejemplo: K7QMX-2HP9D">
            {(a11y) => (
              <Input
                {...a11y}
                autoComplete="off"
                autoCapitalize="characters"
                className="font-mono uppercase"
                value={code}
                onChange={(event) => {
                  setCode(event.target.value);
                }}
              />
            )}
          </Field>
        )}
        <Button
          variant="brand"
          type="submit"
          className="w-full"
          loading={busy}
          disabled={code.length === 0}
        >
          Verificar
        </Button>
      </form>
      <Button
        variant="link"
        className="w-full"
        onClick={() => {
          setMode(mode === 'totp' ? 'recovery' : 'totp');
          setCode('');
          setError(null);
        }}
      >
        {mode === 'totp'
          ? '¿No tienes tu teléfono? Usa un código de respaldo'
          : 'Usar el código de la aplicación'}
      </Button>
    </AuthLayout>
  );
}
